import type { ComponentProps, FormEvent } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import Switch from "@/v2-ui/switch"
import Textarea from "@/v2-ui/textarea"
import Select from "@/v2-ui/select"
import {
  PROVIDER_OVERRIDE_NONE_VALUE,
  type StatusProvider,
  type UptimeEditPublicFormState,
} from "@/v2-backoffice-client/uptime/editPublic/uptime.editPublic.utils"

export type UptimeEditPublicFormProps = Omit<
  ComponentProps<"form">,
  "onSubmit" | "onChange"
> & {
  value: UptimeEditPublicFormState
  providers: StatusProvider[]
  defaultDisplayName: string
  defaultProviderName: string
  isSaving?: boolean
  onChange(next: UptimeEditPublicFormState): void
  onSubmit(value: UptimeEditPublicFormState, e: FormEvent): void
}

// UptimeEditPublicForm is the controlled inline form that drives
// status.TargetOverrides. The parent owns all state so the same
// form value can also feed the side-by-side public preview without
// us having to sync two stores.
function UptimeEditPublicForm(props: UptimeEditPublicFormProps) {
  const {
    value,
    providers,
    defaultDisplayName,
    defaultProviderName,
    isSaving,
    onChange,
    onSubmit,
    className,
    id,
    ...formProps
  } = props

  const selectValue =
    value.providerId === "" ? PROVIDER_OVERRIDE_NONE_VALUE : value.providerId

  return (
    <form
      id={id ?? "uptime-edit-public-form"}
      className={cn("uptime-edit-public-form", className)}
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit(value, e)
      }}
      {...formProps}
    >
      <div className="uptime-edit-public-form-field">
        <Label htmlFor="uptime-edit-public-displayName">Display name</Label>
        <Input
          id="uptime-edit-public-displayName"
          type="text"
          placeholder={defaultDisplayName}
          value={value.displayName}
          disabled={isSaving}
          onChange={(e) => {
            onChange({ ...value, displayName: e.target.value })
          }}
        />
        <p className="uptime-edit-public-form-hint">
          Falls back to the probe&apos;s humanised name when blank.
        </p>
      </div>

      <div className="uptime-edit-public-form-field">
        <Label htmlFor="uptime-edit-public-description">
          Public description
        </Label>
        <Textarea
          id="uptime-edit-public-description"
          rows={3}
          placeholder="Short customer-facing description of what this service does."
          value={value.description}
          disabled={isSaving}
          onChange={(e) => {
            onChange({ ...value, description: e.target.value })
          }}
        />
        <p className="uptime-edit-public-form-hint">
          Shown underneath the service name on status.placepoint.no.
        </p>
      </div>

      <div className="uptime-edit-public-form-field">
        <Label htmlFor="uptime-edit-public-provider">Provider</Label>
        <Select
          value={selectValue}
          disabled={isSaving}
          onValueChange={(next) => {
            onChange({
              ...value,
              providerId: next === PROVIDER_OVERRIDE_NONE_VALUE ? "" : next,
            })
          }}
        >
          <Select.Trigger id="uptime-edit-public-provider">
            <Select.Value placeholder="Auto-detect from URL" />
          </Select.Trigger>
          <Select.Content>
            <Select.Item value={PROVIDER_OVERRIDE_NONE_VALUE}>
              Auto-detect ({defaultProviderName})
            </Select.Item>
            {providers.map((p) => (
              <Select.Item key={p.Id} value={p.Id}>
                {p.Name}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
        <p className="uptime-edit-public-form-hint">
          Override the auto-detected provider when the URL doesn&apos;t map
          cleanly (e.g. a custom domain in front of a known vendor).
        </p>
      </div>

      <Label
        className="uptime-edit-public-form-toggle"
        htmlFor="uptime-edit-public-isPublicHidden"
      >
        <div className="uptime-edit-public-form-toggle-text">
          <span className="uptime-edit-public-form-toggle-title">
            Hide from public status page
          </span>
          <span className="uptime-edit-public-form-toggle-hint">
            The probe still runs and feeds backoffice metrics; customers simply
            don&apos;t see it.
          </span>
        </div>
        <Switch
          id="uptime-edit-public-isPublicHidden"
          checked={value.isPublicHidden}
          disabled={isSaving}
          onCheckedChange={(checked) => {
            onChange({ ...value, isPublicHidden: checked === true })
          }}
        />
      </Label>
    </form>
  )
}

export default UptimeEditPublicForm
